import React, {useRef} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, DropdownHandler, Icon, Text} from '@forecast-it/design-system';

// Each row is icon-then-label on one line. The gap is set here rather than relying on the item's own
// padding, so the two rows align with each other regardless of glyph width.
const MenuRow = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
	white-space: nowrap;
`;
// The trigger renders as a glyph with no text, and the design system's Button derives its own
// `aria-label` from its icon name and applies it *after* the caller's props — so an `aria-label`
// passed here would be silently replaced, leaving the button announced as "more". `aria-labelledby`
// survives that override, so the accessible name comes from this off-screen span instead.
const TriggerLabel = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const MENU_TRIGGER_TEST_ID = 'nova-agent-menu';

// One id per mounted menu, rather than a module-level constant. Only one agent is selected at a
// time today, so duplicates are not reachable — but two of these mounted together would put the same
// id in the DOM twice and quietly break the `aria-labelledby` wiring above, and a counter makes that
// unreachable by construction instead of by circumstance. React 16 has no `useId`.
let menuLabelSequence = 0;

function useMenuLabelId(): string {
	const idRef = useRef<string | null>(null);
	if (idRef.current === null) {
		menuLabelSequence += 1;
		idRef.current = `nova-agent-menu-label-${menuLabelSequence}`;
	}
	return idRef.current;
}

interface Props {
	// Copies the selected agent and moves focus to the copy. The unsaved-changes guard lives in the
	// caller, not here — this is only the menu.
	onDuplicate: () => void;
	// Opens the delete confirmation. Choosing this item destroys nothing on its own.
	onDelete: () => void;
}

export function NovaStudioAgentMenu({onDuplicate, onDelete}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const menuLabelId = useMenuLabelId();

	return (
		<>
			<TriggerLabel id={menuLabelId}>{formatMessage({id: 'nova.agentMenu.label'})}</TriggerLabel>
			<DropdownHandler>
				<DropdownHandler.Trigger>
					<Button
						emphasis="low"
						icon="more"
						aria-labelledby={menuLabelId}
						data-cy={MENU_TRIGGER_TEST_ID}
						data-testid={MENU_TRIGGER_TEST_ID}
					/>
				</DropdownHandler.Trigger>
				{/* Right-aligned under the trigger, which sits at the right edge of the header. */}
				<DropdownHandler.Content offset={4} align="end">
					<DropdownHandler.Item data-cy="nova-agent-menu-duplicate" onClick={onDuplicate}>
						<MenuRow>
							{/* The design draws a two-overlapping-pages "duplicate" glyph, which the
							    design system's icon set does not carry. `new` is the closest available
							    meaning — this action does produce a new agent — where `copyLink` would
							    be worse, reading as copying a URL. */}
							<Icon icon="new" size="s" />
							{formatMessage({id: 'nova.agentMenu.duplicate'})}
						</MenuRow>
					</DropdownHandler.Item>
					<DropdownHandler.Separator />
					<DropdownHandler.Item data-cy="nova-agent-menu-delete" onClick={onDelete}>
						{/* Destructive styling comes from the design system's own semantic `error` colour on
						    both the glyph and the label. The dropdown item has no destructive variant,
						    but neither piece needs a hand-picked hex to say "this one destroys things". */}
						<MenuRow>
							<Icon icon="trash" size="s" color="error" />
							<Text color="error">{formatMessage({id: 'nova.agentMenu.delete'})}</Text>
						</MenuRow>
					</DropdownHandler.Item>
				</DropdownHandler.Content>
			</DropdownHandler>
		</>
	);
}
